/**
 * =============================================================================
 * useHeartbeat — the ONE place that decides WHEN a player sends a progress heartbeat
 * =============================================================================
 *
 * Sending is reportProgress (App.jsx, via AuthContext). This hook only decides when.
 * It replaces the heartbeat logic that used to be copied into all six players
 * (WidevinePlayers.jsx and FairPlayPlayers.jsx: desktop, tablet, phone). The rules are
 * exactly the ones those copies had:
 *
 *   While playing (called from the player's timeupdate handler):
 *     send when there is a videoId, duration > 0, position >= 1, and more than
 *     10,000 ms have passed since this player's last send.
 *
 *   Final send (the player unmounts, or videoId / reportProgress change):
 *     send the last known position, if duration > 0 and position >= 1, with the
 *     PREVIOUS videoId and reportProgress — the same moments and values as the old
 *     per-player useEffect, because the effect below has the same dependencies.
 *
 *   Each send also carries `played` (4th argument to reportProgress): the whole seconds
 *   of video actually played since the previous send, for the viewing counter (admin
 *   step 1). The rules for WHEN to send are unchanged by it.
 *
 *   And `view` (5th argument): true on exactly one send per viewing session, the first
 *   one after the session reaches VIEW_SECONDS of video actually watched (or, for a
 *   lesson shorter than that, nearly all of it). A session is this player on this
 *   videoId, from its first update until its final send.
 *
 * USAGE (in a player, where its old final-send effect was):
 *   const heartbeatOnTime = useHeartbeat(videoId);
 *   ...inside timeupdate, after latestRef.current = { pos: t, dur: d }:
 *   heartbeatOnTime(t, d, playbackRate, isPlaying);   // isPlaying = not paused and not mid-seek
 *   ...and on the media element's `seeking` event:
 *   heartbeatOnTime.seeking();
 *
 * The players keep their own latestRef: other code (the playback guard, the link
 * refresh resume) reads it. This hook keeps its own copy of the last position.
 *
 * createHeartbeat() is the plain, React-free core, so it can be tested on its own.
 * =============================================================================
 */

import { useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

export const HEARTBEAT_INTERVAL_MS = 10000;
export const MAX_PLAYED_RANGES = 20;    // the content API's limits for one heartbeat
export const MAX_PLAYED_SECONDS = 60;
export const VIEW_SECONDS = 20;         // a view = 20 seconds of video watched in one session (Vinay, October 24)

// Seconds a session must watch to count as a view: 20, or for a shorter lesson its whole length less one
// second (second 0 is usually never fully played, so it can't be required).
export function viewThreshold(dur) {
  return Math.min(VIEW_SECONDS, Math.max(1, Math.floor(dur) - 1));
}

/**
 * Seconds actually played, as [firstSecond, lastSecond] ranges (inclusive, in the order played).
 *   - Second s counts when normal playback carries the position across s + 1, so only fully
 *     played seconds count (a partly played second is dropped: undercount, never overcount).
 *   - Normal playback: playing, and since the previous tick the position moved forwards by at most
 *     (time passed × speed) + 1 second. Anything else (a seek or skip, going backwards, a paused
 *     or mid-seek tick) ends the run; counting starts again from the next whole second after the
 *     new position. Every seek also ends the run through seeking(), so a skip is never counted,
 *     even one made while the video was stuck buffering (when "time passed" grows with no ticks).
 */
export function createPlayedTracker() {
  let prev = null;        // { pos, now } of the last tick in the current run; null = no run
  let runStart = 0;       // position where the current run began
  let open = null;        // the range the current run is extending (it's the last one in `ranges`)
  let ranges = [];
  let total = 0;          // seconds counted since the last reset (this viewing session)

  return {
    tick(pos, rate, playing, now) {
      if (!playing || !Number.isFinite(pos)) { prev = null; open = null; return; }
      const speed = Number.isFinite(rate) && rate > 0 ? rate : 1;
      if (prev && pos >= prev.pos && pos - prev.pos <= ((now - prev.now) / 1000) * speed + 1) {
        const lo = Math.max(Math.floor(prev.pos), Math.ceil(runStart));
        const hi = Math.floor(pos) - 1;
        if (hi >= lo) {
          total += hi - lo + 1;
          if (open && open[1] === lo - 1) open[1] = hi;
          else {
            open = [lo, hi];
            ranges.push(open);
            if (ranges.length > MAX_PLAYED_RANGES) ranges.shift();
          }
        }
      } else {
        runStart = pos;
        open = null;
      }
      prev = { pos, now };
    },
    // The ranges since the last take (the most recent ones, within the content API's limits); starts a new list.
    take() {
      const out = [];
      let total = 0;
      for (let i = ranges.length - 1; i >= 0 && out.length < MAX_PLAYED_RANGES && total < MAX_PLAYED_SECONDS; i--) {
        const [a, b] = ranges[i];
        const first = Math.max(a, b - (MAX_PLAYED_SECONDS - total) + 1);
        out.unshift([first, b]);
        total += b - first + 1;
      }
      ranges = [];
      open = null;
      return out;
    },
    // The player started a seek: end the run (the next tick starts a new one at the new position).
    seeking() { prev = null; open = null; },
    // Seconds counted in this session (including ones already taken).
    total() { return total; },
    // Forget everything (the video changed or the player is going away).
    reset() { prev = null; open = null; ranges = []; total = 0; },
  };
}

export function createHeartbeat() {
  let lastBeat = 0;
  let latest = { pos: 0, dur: 0 };
  const played = createPlayedTracker();
  let viewSent = false;   // this session's view has been sent

  // true exactly once per session: when the session has watched enough and its view hasn't gone yet.
  const viewDue = (dur) => !viewSent && played.total() >= viewThreshold(dur);

  return {
    // A position update from the player.
    onTime(send, videoId, pos, dur, now, rate, playing) {
      latest = { pos, dur };
      played.tick(pos, rate, playing, now);
      if (send && videoId && dur > 0 && pos >= 1 && now - lastBeat > HEARTBEAT_INTERVAL_MS) {
        lastBeat = now;
        const view = viewDue(dur);
        if (view) viewSent = true;
        send(videoId, pos, dur, played.take(), view);
      }
    },
    // The player started a seek.
    seeking() { played.seeking(); },
    // The final send: last known position, if there is one worth saving.
    final(send, videoId) {
      const { pos, dur } = latest;
      const seconds = played.take();
      const view = dur > 0 && viewDue(dur);
      played.reset();   // the next tick belongs to a different video, or to nothing: a new session
      viewSent = false;
      if (send && videoId && dur > 0 && pos >= 1) send(videoId, pos, dur, seconds, view);
    },
  };
}

export function useHeartbeat(videoId) {
  const { reportProgress } = useAuth();
  const coreRef = useRef(null);
  if (coreRef.current === null) coreRef.current = createHeartbeat();
  const core = coreRef.current;

  // Same dependencies as the old per-player effect ([reportProgress, videoId]; core never changes),
  // so the final send fires at the same moments with the same values.
  // The cleanup also ends the viewing session (views). reportProgress is stable (App.jsx: useCallback on a
  // useMemo'd api) and players are keyed by video, so this runs only when a player closes or its lesson changes.
  // If reportProgress ever stopped being stable, every change would start a new session and count extra views.
  useEffect(() => () => {
    core.final(reportProgress, videoId);
  }, [core, reportProgress, videoId]);

  const onTime = (pos, dur, rate, playing) => core.onTime(reportProgress, videoId, pos, dur, Date.now(), rate, playing);
  onTime.seeking = () => core.seeking();
  return onTime;
}
